import { Maximize, Minimize, Moon, StickyNote, Sun, X } from 'lucide-react';
import { Button } from '@/design-system';
import type { NodeCategory } from '@/domain/funnel/node-system';
import type { Highlight, PresentationTheme } from '@/domain/presentation/contracts';
import { HighlightNavigator } from './HighlightNavigator';
import { StageFocusControl } from './StageFocusControl';
import { PresentationBranding } from './PresentationBranding';
import type { PresentationBrandingMode } from '@/domain/presentation/contracts';

export interface ScenarioOption {
  id: string | null;
  label: string;
}

export interface PresentationHeaderProps {
  title: string;
  brandingMode: PresentationBrandingMode;
  companyName?: string;
  logoUrl?: string;
  scenarioOptions: ScenarioOption[];
  scenarioId: string | null;
  onScenarioChange: (id: string | null) => void;
  categories: readonly NodeCategory[];
  stageFocusCategory: NodeCategory | null;
  onStageFocusChange: (category: NodeCategory | null) => void;
  activeHighlight: Highlight | null;
  hasHighlights: boolean;
  onPreviousHighlight: () => void;
  onNextHighlight: () => void;
  onOverview: () => void;
  notesOpen: boolean;
  onToggleNotes?: () => void;
  showNotesControl?: boolean;
  theme: PresentationTheme;
  onToggleTheme: () => void;
  fullscreenSupported: boolean;
  fullscreenActive: boolean;
  onToggleFullscreen: () => void;
  onExit?: () => void;
}

/** SPEC-011 §63/plan §17 — minimal header: title, scenario, focus controls, notes, theme, fullscreen, exit. */
export function PresentationHeader({
  title,
  brandingMode,
  companyName,
  logoUrl,
  scenarioOptions,
  scenarioId,
  onScenarioChange,
  categories,
  stageFocusCategory,
  onStageFocusChange,
  activeHighlight,
  hasHighlights,
  onPreviousHighlight,
  onNextHighlight,
  onOverview,
  notesOpen,
  onToggleNotes,
  showNotesControl = true,
  theme,
  onToggleTheme,
  fullscreenSupported,
  fullscreenActive,
  onToggleFullscreen,
  onExit,
}: PresentationHeaderProps) {
  return (
    <header className="flex flex-wrap items-center justify-between gap-2 border-b border-border-subtle bg-surface px-4 py-2">
      <div className="flex min-w-0 items-center gap-3">
        <PresentationBranding mode={brandingMode} companyName={companyName} logoUrl={logoUrl} />
        <h1 className="truncate text-sm font-semibold text-text-primary">{title}</h1>
        {scenarioOptions.length > 1 ? (
          <label className="flex items-center gap-1 text-xs text-text-secondary">
            Cenário
            <select
              aria-label="Cenário da apresentação"
              value={scenarioId ?? ''}
              onChange={(e) => onScenarioChange(e.target.value || null)}
              className="rounded-md border border-border bg-surface-2 px-2 py-1 text-xs text-text-primary"
            >
              {scenarioOptions.map((option) => (
                <option key={option.id ?? 'base'} value={option.id ?? ''}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>
        ) : null}
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <StageFocusControl
          categories={categories}
          active={stageFocusCategory}
          onSelect={onStageFocusChange}
        />
        <HighlightNavigator
          active={activeHighlight}
          hasHighlights={hasHighlights}
          onPrevious={onPreviousHighlight}
          onNext={onNextHighlight}
          onOverview={onOverview}
        />
        {showNotesControl && onToggleNotes ? (
          <Button
            variant="secondary"
            aria-label={notesOpen ? 'Ocultar observações' : 'Mostrar observações'}
            onClick={onToggleNotes}
          >
            <StickyNote size={14} />
          </Button>
        ) : null}
        <Button
          variant="secondary"
          aria-label={theme === 'dark' ? 'Mudar para tema claro' : 'Mudar para tema escuro'}
          onClick={onToggleTheme}
        >
          {theme === 'dark' ? <Sun size={14} /> : <Moon size={14} />}
        </Button>
        {fullscreenSupported ? (
          <Button
            variant="secondary"
            aria-label={fullscreenActive ? 'Sair da tela cheia' : 'Entrar em tela cheia'}
            onClick={onToggleFullscreen}
          >
            {fullscreenActive ? <Minimize size={14} /> : <Maximize size={14} />}
          </Button>
        ) : null}
        {onExit ? (
          <Button variant="secondary" aria-label="Sair da apresentação" onClick={onExit}>
            <X size={14} />
          </Button>
        ) : null}
      </div>
    </header>
  );
}
